import React from "react";
import { Plus } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FormLabel } from "@/components/ui/form";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { getTraitIcon, getTraitColor, sortTraitsByTypeAndName } from "@/lib/traitIcons";
import { EntryConfigUnion } from "@/types/entryConfig";
import AddTraitDialog from "./AddTraitDialog";
import { filterEntryConfigsForSelection } from "@/constants/entryConfigVisibility";

interface EntryConfigData {
  entryConfigId: string;
  minNumSamples: number;
}

interface TraitSelectorProps {
  entryConfigs: EntryConfigUnion[];
  entryConfigsLoading: boolean;
  selectedEntryConfigData: EntryConfigData[];
  selectedEntryConfigIds: string[];
  onToggleEntryConfig: (entryConfigId: string) => void;
  onUpdateMinNumSamples: (entryConfigId: string, minNumSamples: number) => void;
  showAddTraitDialog: boolean;
  onShowAddTraitDialogChange: (show: boolean) => void;
  onTraitAdded: (trait: Omit<EntryConfigUnion, "id" | "createdAt" | "createdBy" | "updatedAt" | "updatedBy">) => void;
  /** Set of trait IDs that were added in the field (have userVisibility) */
  fieldAddedTraitIds?: Set<string>;
  /** Whether the user can add new traits (editors and admins only, not collectors) */
  canAddTrait?: boolean;
  /**
   * When set, the user cannot edit the task's traits at all — toggle
   * selection, remove, or change samples — and this string explains why in
   * the controls' tooltips (e.g. a collector editing a task they didn't
   * create). Removing a trait does NOT delete its collected data; the data
   * is hidden and reappears if the trait is re-added.
   */
  editLockedReason?: string;
}

export const TraitSelector: React.FC<TraitSelectorProps> = ({
  entryConfigs,
  entryConfigsLoading,
  selectedEntryConfigData,
  selectedEntryConfigIds,
  onToggleEntryConfig,
  onUpdateMinNumSamples,
  showAddTraitDialog,
  onShowAddTraitDialogChange,
  onTraitAdded,
  fieldAddedTraitIds,
  canAddTrait = true,
  editLockedReason,
}) => {
  // If the user can't edit this task's traits, no trait is interactive.
  // Removing a selected trait only hides its collected data (preserved;
  // reappears on re-add), so anyone who can edit the task's traits may
  // remove them.
  const blocked = editLockedReason !== undefined;

  if (entryConfigsLoading) {
    return (
      <div className="mt-4 space-y-4">
        <div>
          <FormLabel>Select Traits to Measure</FormLabel>
          <div className="mt-2 text-sm text-muted-foreground">Loading available traits...</div>
        </div>
      </div>
    );
  }

  // Split traits into system and user-created.
  // Archived traits are hidden from the picker, EXCEPT any already selected on
  // the task being edited (keepIds) so they stay visible/checked and can't be
  // silently dropped — they just can't be freshly added.
  const filteredConfigs = filterEntryConfigsForSelection(entryConfigs, {
    keepIds: selectedEntryConfigIds,
  });
  const systemTraits = sortTraitsByTypeAndName(filteredConfigs.filter(config => config.isSystem === true));
  const userTraits = sortTraitsByTypeAndName(filteredConfigs.filter(config => !config.isSystem));

  const renderTraitGrid = (traits: typeof filteredConfigs) => {
    return traits.map((entryConfig) => {
      const isSelected = selectedEntryConfigIds.includes(entryConfig.id!);
      const configData = selectedEntryConfigData.find(d => d.entryConfigId === entryConfig.id);
      const TraitIcon = getTraitIcon(entryConfig.type);
      const traitColor = getTraitColor(entryConfig.type);
      // Show asterisk for field-added traits that are currently selected
      const isFieldAdded = isSelected && fieldAddedTraitIds?.has(entryConfig.id!);

      return (
        <div key={entryConfig.id} className="flex items-center gap-2">
          <Badge
            variant={isSelected ? "default" : "outline"}
            title={editLockedReason}
            className={cn(
              "text-sm py-1 px-3 flex-1 flex items-center gap-2",
              blocked ? "cursor-not-allowed" : "cursor-pointer",
              isSelected
                ? "bg-agri-green hover:bg-agri-green-dark"
                : "hover:bg-agri-green-light/20"
            )}
            onClick={() => {
              if (blocked) return;
              onToggleEntryConfig(entryConfig.id!);
            }}>
            <TraitIcon className={cn("h-4 w-4 flex-shrink-0", isSelected ? "" : traitColor)} aria-hidden="true" />
            <span>{entryConfig.name}{isFieldAdded && " *"}</span>
          </Badge>
          {isSelected && (
            <div className="flex items-center gap-1">
              <Label className="text-xs">Samples:</Label>
              <Input
                type="number"
                min="1"
                value={configData?.minNumSamples || 1}
                onChange={(e) => onUpdateMinNumSamples(entryConfig.id!, parseInt(e.target.value) || 1)}
                disabled={blocked}
                className="w-16 h-7 text-xs"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          )}
        </div>
      );
    });
  };

  return (
    <>
      <div className="mt-4 space-y-4">
        <div className="flex items-center justify-between">
          <FormLabel>Select Traits to Measure</FormLabel>
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <span tabIndex={!canAddTrait ? 0 : undefined}>
                <Button
                  type="button"
                  variant="default"
                  size="sm"
                  onClick={() => onShowAddTraitDialogChange(true)}
                  disabled={!canAddTrait}
                  className={!canAddTrait ? "opacity-50 cursor-not-allowed pointer-events-none" : ""}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add New Trait
                </Button>
              </span>
            </TooltipTrigger>
            {!canAddTrait && (
              <TooltipContent>
                <p>Data collectors cannot add new traits.</p>
                <p className="text-xs text-muted-foreground">Only editors and admins can add traits.</p>
              </TooltipContent>
            )}
          </Tooltip>
        </div>

        {/* Glean AI Traits (System Traits) */}
        {systemTraits.length > 0 && (
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-2">Glean AI Traits</h3>
            <div className="grid grid-cols-2 gap-2">
              {renderTraitGrid(systemTraits)}
            </div>
          </div>
        )}

        {/* Organization Traits (User-Created) */}
        {userTraits.length > 0 && (
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-2">Organization Traits</h3>
            <div className="grid grid-cols-2 gap-2">
              {renderTraitGrid(userTraits)}
            </div>
          </div>
        )}

        {/* Legend for field-added traits */}
        {fieldAddedTraitIds && fieldAddedTraitIds.size > 0 && (
          <p className="text-xs text-muted-foreground">
            * Added in field while collecting data
          </p>
        )}
      </div>

      <AddTraitDialog
        open={showAddTraitDialog}
        onOpenChange={onShowAddTraitDialogChange}
        onTraitAdded={onTraitAdded}
        existingNames={entryConfigs.map(e => e.name)}
      />
    </>
  );
};
